// =============================================================================
// src/views/mapa-eleitoral/markup.ts — Marcacao FIEL da Tela 01 (Mapa Eleitoral).
//
// Proveniencia: unified.html #section-mapa-eleitoral (linhas 294-386). String HTML
//   reproduzida 1:1 — mesmas tags/classes/ids/atributos/texto/SVG inline, incluindo
//   o onclick="sendPrompt(...)" inline que segue funcionando via window.sendPrompt.
//   NAO alterar markup: o constraint do usuario e paridade visual exata.
//
// SRP: este arquivo so produz a string da area; binding e estado ficam em index.ts.
// =============================================================================

import { buildProvenanceFooterMarkup } from '../../data/data-provenance';
/** Painel de camadas: modo de visualizacao, toggles socioeconomicos e exibicao. */
function renderMapaPanel(): string {
  return (
    '<div class="u-panel">' +
    renderVisualizationModeBlock() +
    renderSocioeconomicTogglesBlock() +
    renderDisplayToggleBlock() +
    renderPanelCtaButtons() +
    '</div>'
  );
}

/** Bloco "Modo de visualizacao" com os 4 thumbs de camada (SVG inline preservado). */
function renderVisualizationModeBlock(): string {
  return (
    '<div>' +
    '<div class="u-panel-section-label">Modo de visualização</div>' +
    '<div class="u-layer-grid">' +
    '<div class="u-layer-thumb active" data-layer="partido">' +
    '<svg viewBox="0 0 80 80"><rect width="80" height="80" fill="#F1EFE8"/><polygon points="0,0 50,0 80,30 80,80 30,80 0,50" fill="#1D9E75" opacity="0.7"/><polygon points="50,0 80,0 80,30" fill="#185FA5" opacity="0.7"/><polygon points="0,50 30,80 0,80" fill="#534AB7" opacity="0.7"/></svg>' +
    '<span class="layer-name">Partido</span>' +
    '</div>' +
    '<div class="u-layer-thumb" data-layer="heatmap-pct">' +
    '<svg viewBox="0 0 80 80"><rect width="80" height="80" fill="#FFF7F0"/><circle cx="40" cy="40" r="35" fill="url(#hg)" opacity="0.8"/><defs><radialGradient id="hg"><stop offset="0%" stop-color="#E24B4A"/><stop offset="100%" stop-color="#EF9F27" stop-opacity="0"/></radialGradient></defs></svg>' +
    '<span class="layer-name">Heatmap %</span>' +
    '</div>' +
    '<div class="u-layer-thumb" data-layer="vulnerabilidade">' +
    '<svg viewBox="0 0 80 80"><rect width="80" height="80" fill="#FFF9F0"/><rect x="5" y="5" width="33" height="33" rx="3" fill="#EF9F27" opacity="0.5"/><rect x="42" y="5" width="33" height="33" rx="3" fill="#EF9F27" opacity="0.8"/><rect x="5" y="42" width="33" height="33" rx="3" fill="#BA7517" opacity="0.6"/><rect x="42" y="42" width="33" height="33" rx="3" fill="#EF9F27" opacity="0.3"/></svg>' +
    '<span class="layer-name">Vulnerabilidade</span>' +
    '</div>' +
    '<div class="u-layer-thumb" data-layer="pertencimento">' +
    '<svg viewBox="0 0 80 80"><rect width="80" height="80" fill="#F0F8FF"/><circle cx="25" cy="40" r="20" fill="#185FA5" opacity="0.5"/><circle cx="55" cy="40" r="20" fill="#1D9E75" opacity="0.5"/></svg>' +
    '<span class="layer-name">Pertencimento</span>' +
    '</div>' +
    '</div>' +
    '</div>'
  );
}

/** Bloco "Camadas socioeconomicas" com 5 toggle-rows (estados on/off como no original). */
function renderSocioeconomicTogglesBlock(): string {
  return (
    '<div>' +
    '<div class="u-panel-section-label">Camadas socioeconômicas</div>' +
    '<div class="u-toggle-row">' +
    '<div class="toggle-label"><i class="ti ti-school"></i> Educação IDEB</div>' +
    '<div class="u-switch on"></div>' +
    '</div>' +
    '<div class="u-toggle-row">' +
    '<div class="toggle-label"><i class="ti ti-shield"></i> Segurança</div>' +
    '<div class="u-switch"></div>' +
    '</div>' +
    '<div class="u-toggle-row">' +
    '<div class="toggle-label"><i class="ti ti-currency-dollar"></i> Renda média</div>' +
    '<div class="u-switch on"></div>' +
    '</div>' +
    '<div class="u-toggle-row">' +
    '<div class="toggle-label"><i class="ti ti-building-church"></i> Presença religiosa</div>' +
    '<div class="u-switch"></div>' +
    '</div>' +
    '<div class="u-toggle-row">' +
    '<div class="toggle-label"><i class="ti ti-shopping-bag"></i> Comércio local</div>' +
    '<div class="u-switch"></div>' +
    '</div>' +
    '</div>'
  );
}

/** Bloco "Exibicao" com o toggle da Timeline historica (#timelineToggle). */
function renderDisplayToggleBlock(): string {
  return (
    '<div>' +
    '<div class="u-panel-section-label">Exibição</div>' +
    '<div class="u-toggle-row">' +
    '<div class="toggle-label"><i class="ti ti-timeline"></i> Timeline histórica</div>' +
    '<div class="u-switch on" id="timelineToggle"></div>' +
    '</div>' +
    '</div>'
  );
}

/** CTAs do painel: "Comparar grupos" e "Salvar análise" (onclick inline preservado). */
function renderPanelCtaButtons(): string {
  return (
    '<div style="display:flex;gap:8px">' +
    '<button class="u-cta-btn" style="flex:1" onclick="sendPrompt(\'Comparar grupos políticos na zona selecionada\')"><i class="ti ti-git-compare"></i> Comparar grupos</button>' +
    '<button class="u-cta-btn secondary" onclick="sendPrompt(\'Salvar análise atual\')"><i class="ti ti-bookmark"></i> Salvar análise</button>' +
    '</div>'
  );
}

/** Timeline historica: play, track com markers 2014-2024 e controle de velocidade. */
function renderTimeline(): string {
  return (
    '<div class="u-timeline">' +
    '<button class="u-play-btn" id="u-playBtn"><i class="ti ti-player-play"></i></button>' +
    '<div class="u-timeline-track">' +
    '<div class="u-timeline-line"></div>' +
    '<div class="u-timeline-progress" id="u-tl-progress" style="width:80%"></div>' +
    '<div class="u-tl-marker" data-year="2014" style="left:0%"><span class="u-timeline-label">2014</span></div>' +
    '<div class="u-tl-marker event" data-year="2016" style="left:20%"><span class="u-timeline-label">2016</span></div>' +
    '<div class="u-tl-marker" data-year="2018" style="left:40%"><span class="u-timeline-label">2018</span></div>' +
    '<div class="u-tl-marker event" data-year="2020" style="left:60%"><span class="u-timeline-label">2020</span></div>' +
    '<div class="u-tl-marker" data-year="2022" style="left:80%"><span class="u-timeline-label">2022</span></div>' +
    '<div class="u-tl-marker active" data-year="2024" style="left:100%"><span class="u-timeline-label">2024</span></div>' +
    '</div>' +
    '<div class="u-speed-control">' +
    '<button class="u-speed-btn active" data-speed="1">1x</button>' +
    '<button class="u-speed-btn" data-speed="2">2x</button>' +
    '<button class="u-speed-btn" data-speed="4">4x</button>' +
    '</div>' +
    '</div>'
  );
}

/**
 * Marcacao completa da secao #section-mapa-eleitoral (paridade com unified.html).
 * Concatena header + body (painel + timeline) na mesma ordem/estrutura do original.
 */
export function renderMapaEleitoralMarkup(): string {
  return (
    '<section class="u-section" id="section-mapa-eleitoral" data-section="mapa-eleitoral">' +
    '<div class="u-section-header">' +
    '<div class="u-section-title">Mapa Eleitoral Principal</div>' +
    '<div class="u-section-subtitle">Choropleth interativo com camadas configuráveis e timeline histórica</div>' +
    '</div>' +
    '<div class="u-section-body">' +
    renderMapaPanel() +
    renderTimeline() +
    '</div>' +
    buildProvenanceFooterMarkup('mapa-eleitoral') +
    '</section>'
  );
}
